import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import Select from 'components/ui/Select';
import { Checkbox } from 'components/ui/Checkbox';
import { addStaffMember, updateStaffMember } from 'services/staffService';
import { toast } from 'react-toastify';

const StaffModal = ({ isOpen, onClose, staff, onSuccessful, mode = 'add', business }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contactNo: '',
    status: 'active',
    permissions: [],
    address: '',
    emergencyContact: '',
    emergencyPhone: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const permissionGroups = [
    {
      name: 'Service Management',
      permissions: [
        { id: 'scanning', label: 'Scan Bookings', description: 'Create bookings' },
        { id: 'releasing', label: 'Release Bookings', description: 'Release bookings after work has been completed' }
      ]
    },
    {
      name: 'Business Operations',
      permissions: [
        { id: 'admin', label: 'Full Access', description: 'Full business operations' },
        { id: 'reporting', label: 'View Reporting', description: 'Access business dashboard & creating reports' }
      ]
    }
  ];

  useEffect(() => {
    if (staff && mode === 'edit') {
      const staffPermissions = [];
      if(staff?.admin) staffPermissions.push('admin');
      if(staff?.reporting) staffPermissions.push('reporting');
      if(staff?.scanning) staffPermissions.push('scanning');
      if(staff?.releasing) staffPermissions.push('releasing');

      setFormData({
        ...staff,
        hireDate: staff?.created ? new Date(staff.created)?.toISOString()?.split('T')?.[0] : '',
        permissions: staffPermissions,
        status: staff?.active? "active" : "inactive"
      });
    } else if (mode === 'add') {
      setFormData({
        name: '',
        surname: '',
        email: '',
        contactNo: '',
        status: 'active',
        permissions: [],
        address: '',
        // emergencyContact: '',
        // emergencyPhone: '',
        notes: ''
      });
    }
    setErrors({});
  }, [staff, mode, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePermissionChange = (permissionId, checked) => {
    setFormData(prev => ({
      ...prev,
      permissions: checked 
        ? [...prev?.permissions, permissionId]
        : prev?.permissions?.filter(p => p !== permissionId)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) newErrors.name = 'Name is required';
    if (!formData?.email?.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/?.test(formData?.email)) newErrors.email = 'Invalid email format';
    if (!formData?.contactNo?.trim()) newErrors.contactNo = 'Phone number is required';
    // if (!formData?.role) newErrors.role = 'Role is required';
    // if (!formData?.hireDate) newErrors.hireDate = 'Hire date is required';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    const payload = {
      fullname: formData.name,
      surname: formData.surname,
      emailAddress: formData.email,
      contactNo: formData.contactNo,
      active: formData.status === 'active',
      admin: formData.permissions.includes('admin'),
      reporting: formData.permissions.includes('reporting'),
      releasing: formData.permissions.includes('releasing'),
      scanning: formData.permissions.includes('scanning'),
    }
    
    try{
      if(mode === 'add'){
        await handleAddStaffMember(payload);
      }
      else if(mode === 'edit' && staff){
        await handleUpdateStaffMember(payload);
      }
    }
    catch(error){
      toast.error('An unexpected error occurred. Please try again.');
    }
    setIsSubmitting(false);
  };

  const handleAddStaffMember = async (payload)=>{
    const response = await addStaffMember(business.businessId, payload);
    if(response.success){
      toast.success('Staff member added successfully');
      onSuccessful();
    }
    else{
      toast.error(response.message || 'Failed to add staff member. Please try again.');
    }
  }

  const handleUpdateStaffMember = async (payload)=>{

    if(staff.userId == null){
      toast.error('Invalid staff member. Please try again.');
      return;
    }
    const response = await updateStaffMember(business.businessId, staff.userId, payload);

    if(response.success){
      toast.success('Staff member updated successfully');
      onSuccessful();
    }
    else{
      toast.error(response.message || 'Failed to update staff member. Please try again.');
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-1002 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose}></div>
        
        <div className="relative bg-card border border-border rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {mode === 'add' ? 'Add New Staff Member' : 'Edit Staff Member'}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {mode === 'add' ? 'Enter staff member details and assign permissions' : 'Update staff member information'}
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground flex items-center">
                  <Icon name="User" size={20} className="mr-2" />
                  Basic Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Full Name"
                    type="text"
                    value={formData?.name}
                    onChange={(e) => handleInputChange('name', e?.target?.value)}
                    error={errors?.name}
                    required
                    placeholder="Enter name"
                  />
                  
                  <Input
                    label="Surname"
                    type="text"
                    value={formData?.surname}
                    onChange={(e) => handleInputChange('surname', e?.target?.value)}
                    placeholder="Enter surname"
                    error={errors?.surname}
                    required
                  />
                  
                  <Input
                    label="Email Address"
                    type="email"
                    value={formData?.email}
                    onChange={(e) => handleInputChange('email', e?.target?.value)}
                    error={errors?.email}
                    required
                    placeholder="Enter email address"
                  />
                  
                  <Input
                    label="Phone Number"
                    type="tel"
                    value={formData?.contactNo}
                    onChange={(e) => handleInputChange('contactNo', e?.target?.value)}
                    error={errors?.contactNo}
                    required
                    placeholder="Enter phone number"
                  />
                  {(staff && mode === 'edit') &&
                    <Input
                      label="Hire Date"
                      type="date"
                      value={formData?.hireDate}
                      // onChange={(e) => handleInputChange('hireDate', e?.target?.value)}
                      disabled
                    />
              }
                </div>
              </div>

              {/* Employment Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground flex items-center">
                  <Icon name="Briefcase" size={20} className="mr-2" />
                  Employment Details
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  
                  <Select
                    label="Status"
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' },
                      // { value: 'suspended', label: 'Suspended' }
                    ]}
                    value={formData?.status}
                    onChange={(value) => handleInputChange('status', value)}
                  />
                </div>
              </div>

              {/* Permissions */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground flex items-center">
                  <Icon name="Shield" size={20} className="mr-2" />
                  Permissions
                </h3>
                
                <div className="space-y-6">
                  {permissionGroups?.map((group) => (
                    <div key={group?.name} className="space-y-3">
                      <h4 className="font-medium text-foreground">{group?.name}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {group?.permissions?.map((permission) => (
                          <Checkbox
                            key={permission?.id}
                            label={permission?.label}
                            description={permission?.description}
                            checked={formData?.permissions?.includes(permission?.id)}
                            onChange={(e) => handlePermissionChange(permission?.id, e?.target?.checked)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/30">
              <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                loading={isSubmitting}
                iconName="Save"
                iconPosition="left"
                iconSize={16}
              >
                {mode === 'add' ? 'Add Staff Member' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StaffModal;